/**
 * InfinitSales - Connection Setup Page
 * Full-screen page for WhatsApp connection
 * 
 * UNIFIED VERSION:
 * - wa_personal: QR Code + polling no /status
 * - wa_business: OAuth Facebook SDK + exchange
 * - Ambos redirecionam para /agent-onboarding após conexão
 * 
 * Flow: ChannelSelection → ConnectionSetup → AgentOnboarding → Inbox
 * 
 * ✅ CORREÇÃO APLICADA:
 * - Removido fallback hardcoded para 'wa_personal'
 * - Adicionado refreshUser() após conexão bem-sucedida
 * - Garantido que o canal vem corretamente do backend
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  RefreshCw, 
  Loader2, 
  CheckCircle2, 
  WifiOff, 
  Settings,
  Smartphone,
  Building2,
  MessageSquare,
  History,
  ArrowRight
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import whatsappService from '../../services/wa-personal/whatsappService';
import ProviderPicker from '../../components/wa-personal/ProviderPicker';
import waBusinessService from '../../services/wa-business/waBusinessService';

// ============================================
// META APP CONFIGURATION
// ============================================
const META_APP_ID = process.env.REACT_APP_META_APP_ID || '1833485603931931';
const META_CONFIG_ID = process.env.REACT_APP_META_CONFIG_ID || '1463966647970356';

// ============================================
// CONNECTION STATES
// ============================================
const STATES = {
  LOADING: 'loading',
  WAITING_QR: 'waiting_qr',
  WAITING_OAUTH: 'waiting_oauth',
  EXCHANGING: 'exchanging',
  SYNCING: 'syncing',
  CONNECTED: 'connected',
  ERROR: 'error',
  RECREATING: 'recreating'
};

// ============================================
// WHATSAPP LOGO COMPONENT
// ============================================
const WhatsAppLogo = ({ size = 48, color = "#25D366" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// ============================================
// MAIN COMPONENT
// ============================================
const ConnectionSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colors, theme } = useTheme();
  // ✅ CORREÇÃO: Adicionar refreshUser para atualizar contexto após conexão
  const { user, refreshUser } = useAuth();
  const isDark = theme === 'dark';

  // ✅ CORREÇÃO: Não usar fallback 'wa_personal'
  // Se não soubermos o canal, usar o do user. Se não tiver, redirecionar para onboarding
  const channel = location.state?.channel || user?.default_channel;
  
  // ✅ CORREÇÃO: Se não tiver canal definido, redirecionar para onboarding
  useEffect(() => {
    if (!channel) {
      console.warn('[ConnectionSetup] Canal não definido, redirecionando para onboarding');
      navigate('/onboarding', { replace: true });
    }
  }, [channel, navigate]);
  
  const isWABusiness = channel === 'wa_business';

  // States
  const [connectionState, setConnectionState] = useState(STATES.LOADING);
  const [qrCode, setQrCode] = useState(null);
  const [error, setError] = useState('');
  // Provedor da conexao QR (opcao avancada, recolhida): Evolution padrao, WAHA, Wuzapi
  const [providers, setProviders] = useState([]);
  const [providerSelectable, setProviderSelectable] = useState(false);
  const [currentProvider, setCurrentProvider] = useState(null);
  const [syncProgress, setSyncProgress] = useState(0);
  const [sdkReady, setSdkReady] = useState(false);

  // Refs
  const pollingRef = useRef(null);
  const isMountedRef = useRef(true);
  const metaSignupDataRef = useRef(null);
  const fbInitializedRef = useRef(false);

  // ============================================
  // STYLES (mesmo código anterior - truncado para brevidade)
  // ============================================
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.bgSecondary,
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    content: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 24px',
    },
    card: {
      backgroundColor: colors.bgPrimary,
      borderRadius: '20px',
      padding: '48px',
      maxWidth: '480px',
      width: '100%',
      textAlign: 'center',
      boxShadow: isDark ? 'none' : '0 4px 24px rgba(0,0,0,0.08)',
      border: `1px solid ${colors.borderColor}`,
    },
    iconContainer: {
      width: '80px',
      height: '80px',
      borderRadius: '20px',
      backgroundColor: '#25D36615',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 24px',
      position: 'relative',
    },
    businessBadge: {
      position: 'absolute',
      bottom: '-4px',
      right: '-4px',
      width: '28px',
      height: '28px',
      backgroundColor: '#1877f2',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `3px solid ${colors.bgPrimary}`,
    },
    title: {
      fontSize: '24px',
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '15px',
      color: colors.textSecondary,
      marginBottom: '32px',
      lineHeight: 1.5,
    },
    qrContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '32px',
    },
    qrCode: {
      width: '220px',
      height: '220px',
      borderRadius: '12px',
      border: `1px solid ${colors.borderColor}`,
    },
    qrPlaceholder: {
      width: '220px',
      height: '220px',
      borderRadius: '12px',
      backgroundColor: colors.bgTertiary,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
      color: colors.textSecondary,
    },
    steps: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      marginBottom: '24px',
      textAlign: 'left',
    },
    step: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '14px',
      color: colors.textSecondary,
    },
    stepNumber: {
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      fontSize: '13px',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    refreshButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      border: 'none',
      backgroundColor: 'transparent',
      color: colors.accentPrimary,
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500,
    },
    retryButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 24px',
      border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgPrimary,
      color: colors.textPrimary,
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500,
    },
    features: {
      marginBottom: '28px',
      textAlign: 'left',
    },
    featureItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '14px',
      padding: '14px 0',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    featureItemLast: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '14px',
      padding: '14px 0',
      borderBottom: 'none',
    },
    featureIcon: {
      width: '40px',
      height: '40px',
      backgroundColor: colors.bgTertiary,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    featureContent: {
      flex: 1,
    },
    featureTitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: colors.textPrimary,
      marginBottom: '2px',
    },
    featureDesc: {
      fontSize: '13px',
      color: colors.textSecondary,
      lineHeight: 1.4,
    },
    connectBtn: {
      width: '100%',
      padding: '16px 24px',
      backgroundColor: '#25D366',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.2s',
    },
    connectBtnDisabled: {
      backgroundColor: colors.bgTertiary,
      color: colors.textTertiary,
      cursor: 'not-allowed',
    },
    syncContainer: {
      textAlign: 'center',
    },
    syncIcon: {
      color: colors.accentPrimary,
      marginBottom: '24px',
    },
    progressBar: {
      width: '100%',
      height: '6px',
      backgroundColor: colors.bgTertiary,
      borderRadius: '3px',
      overflow: 'hidden',
      marginBottom: '16px',
    },
    progressFill: {
      height: '100%',
      backgroundColor: colors.accentPrimary,
      borderRadius: '3px',
      transition: 'width 0.3s ease',
    },
    progressText: {
      fontSize: '14px',
      color: colors.textSecondary,
    },
    successIcon: {
      color: '#10B981',
      marginBottom: '24px',
    },
  };

  // ============================================
  // STOP POLLING (WA Personal)
  // ============================================
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
      console.log('[ConnectionSetup] Polling parado');
    }
  }, []);

  // ============================================
  // ✅ CORREÇÃO: SYNC PROGRESS COM refreshUser
  // ============================================
  const simulateSyncProgress = useCallback(async () => {
    let progress = 0;
    const interval = setInterval(async () => {
      progress += Math.random() * 15 + 5;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        
        setConnectionState(STATES.CONNECTED);
        
        // ✅ CORREÇÃO: Atualizar o user no contexto ANTES de navegar
        // Isso garante que o AuthContext terá o default_channel correto
        try {
          console.log('[ConnectionSetup] Atualizando user no contexto...');
          const updatedUser = await refreshUser();
          console.log('[ConnectionSetup] User atualizado:', {
            default_channel: updatedUser?.default_channel,
            onboarding_completed: updatedUser?.onboarding_completed
          });
        } catch (err) {
          console.error('[ConnectionSetup] Erro ao atualizar user:', err);
        }
        
        setTimeout(() => {
          // Conta Empresa: members herdam o agente da empresa — pulam o passo
          // /agent-onboarding e caem direto no inbox.
          // ✅ CORREÇÃO: Usar o canal que temos (não recalcular)
          const isMember =
            (user?.company_id && user?.company_role !== 'admin') || user?.created_by_company === true;
          if (isMember) {
            const dest = channel === 'wa_business' ? '/wa-business' : '/inbox';
            navigate(dest, { replace: true });
          } else {
            navigate('/agent-onboarding', {
              state: { channel },
              replace: true,
            });
          }
        }, 1500);
      }
      setSyncProgress(Math.min(progress, 100));
    }, 500);
  }, [navigate, channel, refreshUser]);

  // ============================================
  // WA PERSONAL: CHECK STATUS (polling)
  // ============================================
  const checkStatus = useCallback(async () => {
    if (!isMountedRef.current || isWABusiness) return;
    
    try {
      const result = await whatsappService.getConnectionStatus();
      const data = result?.data || result;
      
      console.log('[ConnectionSetup] Status:', data?.status, '| Connected:', data?.connected);
      
      if (data?.connected) {
        console.log('[ConnectionSetup] ✅ CONECTADO! Iniciando sync...');
        stopPolling();
        setConnectionState(STATES.SYNCING);
        simulateSyncProgress();
      }
    } catch (err) {
      console.error('[ConnectionSetup] Erro no polling:', err);
    }
  }, [stopPolling, simulateSyncProgress, isWABusiness]);

  // ============================================
  // WA PERSONAL: START POLLING
  // ============================================
  const startPolling = useCallback(() => {
    if (isWABusiness) return;
    
    stopPolling();
    checkStatus();
    pollingRef.current = setInterval(checkStatus, 2000);
    console.log('[ConnectionSetup] Polling iniciado (2s)');
  }, [stopPolling, checkStatus, isWABusiness]);

  // ============================================
  // WA PERSONAL: FETCH QR CODE
  // ============================================
  const fetchQRCode = useCallback(async () => {
    if (isWABusiness || !isMountedRef.current) return;
    
    setConnectionState(STATES.LOADING);
    setError('');

    try {
      const statusResult = await whatsappService.getConnectionStatus();
      const statusData = statusResult?.data || statusResult;
      
      if (statusData?.connected) {
        setConnectionState(STATES.SYNCING);
        simulateSyncProgress();
        return;
      }

      const qrResult = await whatsappService.getQRCode();
      const qrData = qrResult?.data || qrResult;
      
      if (!isMountedRef.current) return;

      if (qrData?.connected) {
        setConnectionState(STATES.SYNCING);
        simulateSyncProgress();
        return;
      }
      
      if (qrData?.qrcode) {
        setQrCode(qrData.qrcode);
        setConnectionState(STATES.WAITING_QR);
        startPolling();
      } else {
        const recheckResult = await whatsappService.getConnectionStatus();
        const recheckData = recheckResult?.data || recheckResult;
        
        if (recheckData?.connected) {
          setConnectionState(STATES.SYNCING);
          simulateSyncProgress();
        } else {
          setError('Não foi possível gerar o QR Code');
          setConnectionState(STATES.ERROR);
        }
      }
    } catch (err) {
      console.error('[ConnectionSetup] Erro:', err);
      if (isMountedRef.current) {
        setError(err.response?.data?.detail || 'Erro ao gerar QR Code');
        setConnectionState(STATES.ERROR);
      }
    }
  }, [isWABusiness, simulateSyncProgress, startPolling]);

  // ============================================
  // WA PERSONAL: RECREATE INSTANCE
  // ============================================
  const handleRecreateInstance = useCallback(async (providerId = null) => {
    if (!isMountedRef.current) return;
    const escolhido = typeof providerId === 'string' ? providerId : null;
    
    setConnectionState(STATES.RECREATING);
    setError('');
    stopPolling();

    try {
      const result = await whatsappService.recreateInstance(escolhido);
      const data = result?.data || result;
      
      if (!isMountedRef.current) return;
      if (escolhido) setCurrentProvider(escolhido);
      
      if (data?.qrcode) {
        setQrCode(data.qrcode);
        setConnectionState(STATES.WAITING_QR);
        startPolling();
      } else if (data?.connected) {
        setConnectionState(STATES.SYNCING);
        simulateSyncProgress();
      } else {
        setError('Não foi possível reconfigurar. Tente novamente.');
        setConnectionState(STATES.ERROR);
      }
    } catch (err) {
      console.error('[ConnectionSetup] Erro recreate:', err);
      if (isMountedRef.current) {
        setError(err.response?.data?.detail || 'Erro ao reconfigurar ambiente');
        setConnectionState(STATES.ERROR);
      }
    }
  }, [stopPolling, startPolling, simulateSyncProgress]);

  // Provedores disponiveis (backend com WA_PROVIDER_ABSTRACTION_ENABLED);
  // sem o endpoint a tela segue igual, sem a opcao avancada.
  useEffect(() => {
    if (isWABusiness || !channel) return;
    let vivo = true;
    (async () => {
      try {
        const result = await whatsappService.getProviders();
        const data = result?.data || result || {};
        if (!vivo) return;
        setProviders(Array.isArray(data.providers) ? data.providers : []);
        setProviderSelectable(Boolean(data.selectable));
        setCurrentProvider(data.current || data.default || null);
      } catch (e) {
        console.debug('[ConnectionSetup] providers indisponiveis:', e?.message);
      }
    })();
    return () => { vivo = false; };
  }, [isWABusiness, channel]);

  const renderProviderPicker = (aberto = false) => providerSelectable ? (
    <ProviderPicker
      providers={providers}
      current={currentProvider}
      disabled={connectionState === STATES.RECREATING}
      defaultOpen={aberto}
      onSelect={(id) => handleRecreateInstance(id)}
    />
  ) : null;

  // ============================================
  // WA BUSINESS: FACEBOOK SDK INIT
  // ============================================
  useEffect(() => {
    if (!isWABusiness) return;

    const initFacebookSDK = () => {
      window.fbAsyncInit = function() {
        window.FB.init({
          appId: META_APP_ID,
          autoLogAppEvents: true,
          xfbml: true,
          version: 'v22.0'
        });
        
        fbInitializedRef.current = true;
        setSdkReady(true);
        setConnectionState(STATES.WAITING_OAUTH);
        console.log('[ConnectionSetup] Facebook SDK initialized');
      };

      if (!document.getElementById('facebook-jssdk')) {
        const script = document.createElement('script');
        script.id = 'facebook-jssdk';
        script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        
        script.onerror = () => {
          console.error('[ConnectionSetup] Failed to load Facebook SDK');
          setError('Falha ao carregar SDK do Facebook. Recarregue a página.');
          setConnectionState(STATES.ERROR);
        };
        
        document.body.appendChild(script);
      } else if (window.FB) {
        fbInitializedRef.current = true;
        setSdkReady(true);
        setConnectionState(STATES.WAITING_OAUTH);
      }
    };

    initFacebookSDK();
  }, [isWABusiness]);

  // ============================================
  // WA BUSINESS: POST MESSAGE LISTENER
  // ============================================
  useEffect(() => {
    if (!isWABusiness) return;

    const handleMessage = (event) => {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      ) {
        return;
      }

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          console.log('[ConnectionSetup] WA_EMBEDDED_SIGNUP:', data.event);
          
          if (data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING' || data.event === 'FINISH') {
            const { phone_number_id, waba_id, business_id } = data.data || {};
            
            metaSignupDataRef.current = { 
              phone_number_id, 
              waba_id, 
              business_id,
              isCoexistence: data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
            };
            
          } else if (data.event === 'CANCEL') {
            // Mesmo tratamento do ConnectionModal: tela e erro da Meta no log.
            const { current_step, error_code, error_message, session_id } = data.data || {};
            console.warn('[ConnectionSetup] Signup cancelado', { current_step, error_code, error_message, session_id });
            setError(error_message
              ? `A Meta recusou a conexão: ${error_message}${error_code ? ` (${error_code})` : ''}`
              : 'Conexão cancelada pelo usuário');
            setConnectionState(STATES.ERROR);
            
          } else if (data.event === 'ERROR') {
            setError(data.data?.error_message || 'Erro ao conectar com WhatsApp Business');
            setConnectionState(STATES.ERROR);
          }
        }
      } catch (err) {
        // Ignore non-JSON messages
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isWABusiness]);

  // ============================================
  // WA BUSINESS: EXCHANGE CODE FOR TOKEN
  // ============================================
  const exchangeCodeForToken = useCallback(async (code) => {
    setConnectionState(STATES.EXCHANGING);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const signupData = metaSignupDataRef.current || {};
      
      console.log('[ConnectionSetup] Exchanging code...', {
        hasCode: !!code,
        hasWabaId: !!signupData.waba_id,
      });

      const response = await waBusinessService.exchangeOAuth(
        code,
        signupData.waba_id,
        signupData.phone_number_id,
        signupData.business_id,
        // Sem este argumento o backend assumia coexistência para qualquer
        // FINISH. Mesmo critério do ConnectionModal.
        signupData.isCoexistence === true
      );

      if (response.success) {
        console.log('[ConnectionSetup] ✅ WA Business configurado!');
        setConnectionState(STATES.SYNCING);
        simulateSyncProgress();
      } else {
        throw new Error(response.message || 'Erro ao configurar conta');
      }
      
    } catch (err) {
      console.error('[ConnectionSetup] OAuth exchange error:', err);
      setError(err.response?.data?.detail || err.message || 'Erro ao conectar WhatsApp Business');
      setConnectionState(STATES.ERROR);
    }
  }, [simulateSyncProgress]);

  // ============================================
  // WA BUSINESS: LAUNCH SIGNUP
  // ============================================
  const launchWhatsAppSignup = useCallback(() => {
    if (!window.FB) {
      setError('SDK do Facebook não carregado. Recarregue a página.');
      return;
    }

    setError('');
    setConnectionState(STATES.LOADING);
    metaSignupDataRef.current = null;

    console.log('[ConnectionSetup] Launching WhatsApp Business signup...');

    window.FB.login(
      (response) => {
        console.log('[ConnectionSetup] FB.login response:', response);
        
        if (response.authResponse && response.authResponse.code) {
          exchangeCodeForToken(response.authResponse.code);
        } else {
          setError('Autorização negada ou cancelada');
          setConnectionState(STATES.ERROR);
        }
      },
      {
        config_id: META_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3',
          // Mesmo extras do ConnectionModal (Embedded Signup v4).
          ...(process.env.REACT_APP_META_ES_VERSION
            ? { version: process.env.REACT_APP_META_ES_VERSION }
            : {}),
        }
      }
    );
  }, [exchangeCodeForToken]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  // Initial load
  useEffect(() => {
    // ✅ CORREÇÃO: Só iniciar se tivermos canal definido
    if (!channel) return;
    
    if (!isWABusiness) {
      fetchQRCode();
    }
    
    return () => stopPolling();
  }, [isWABusiness, fetchQRCode, stopPolling, channel]);

  // Auto-refresh QR Code every 30s (WA Personal only)
  useEffect(() => {
    if (connectionState !== STATES.WAITING_QR || isWABusiness) return;

    const timeout = setTimeout(async () => {
      try {
        const result = await whatsappService.getConnectionStatus();
        const data = result?.data || result;
        
        if (data?.connected) {
          stopPolling();
          setConnectionState(STATES.SYNCING);
          simulateSyncProgress();
        } else {
          const qrResult = await whatsappService.getQRCode();
          const qrData = qrResult?.data || qrResult;
          
          if (qrData?.qrcode) {
            setQrCode(qrData.qrcode);
          } else if (qrData?.connected) {
            stopPolling();
            setConnectionState(STATES.SYNCING);
            simulateSyncProgress();
          }
        }
      } catch (err) {
        console.error('[ConnectionSetup] Erro no refresh:', err);
      }
    }, 30000);
    
    return () => clearTimeout(timeout);
  }, [connectionState, qrCode, isWABusiness, stopPolling, simulateSyncProgress]);

  // ============================================
  // RENDER: LOADING
  // ============================================
  const renderLoading = () => (
    <>
      <div style={styles.iconContainer}>
        <Loader2 size={40} color="#25D366" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
      <h2 style={styles.title}>Preparando conexão...</h2>
      <p style={styles.subtitle}>Aguarde enquanto configuramos tudo para você</p>
    </>
  );

  // ============================================
  // RENDER: QR CODE (WA Personal)
  // ============================================
  const renderQRCode = () => (
    <>
      <div style={styles.iconContainer}>
        <WhatsAppLogo size={40} />
      </div>
      <h2 style={styles.title}>Conecte seu WhatsApp</h2>
      <p style={styles.subtitle}>Escaneie o QR Code com seu celular</p>
      
      <div style={styles.qrContainer}>
        {qrCode ? (
          <img
            src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
            alt="QR Code"
            style={styles.qrCode}
          />
        ) : (
          <div style={styles.qrPlaceholder}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
            <span>Gerando QR Code...</span>
          </div>
        )}
      </div>

      <div style={styles.steps}>
        <div style={styles.step}>
          <span style={styles.stepNumber}>1</span>
          <span>Abra o WhatsApp no seu celular</span>
        </div>
        <div style={styles.step}>
          <span style={styles.stepNumber}>2</span>
          <span>Toque em Menu ou Configurações</span>
        </div>
        <div style={styles.step}>
          <span style={styles.stepNumber}>3</span>
          <span>Selecione "Aparelhos conectados"</span>
        </div>
        <div style={styles.step}>
          <span style={styles.stepNumber}>4</span>
          <span>Escaneie este QR Code</span>
        </div>
      </div>

      <button style={styles.refreshButton} onClick={fetchQRCode}>
        <RefreshCw size={16} />
        Gerar novo QR Code
      </button>
      {renderProviderPicker(false)}
    </>
  );

  // ============================================
  // RENDER: OAUTH (WA Business)
  // ============================================
  const renderOAuth = () => (
    <>
      <div style={styles.iconContainer}>
        <WhatsAppLogo size={40} />
        <div style={styles.businessBadge}>
          <Building2 size={14} color="white" />
        </div>
      </div>
      <h2 style={styles.title}>Conectar WhatsApp Business</h2>
      <p style={styles.subtitle}>Conecte seu app e continue usando no celular</p>

      <div style={styles.features}>
        <div style={styles.featureItem}>
          <div style={{ ...styles.featureIcon, backgroundColor: '#25D36615' }}>
            <Smartphone size={20} color="#25D366" />
          </div>
          <div style={styles.featureContent}>
            <div style={styles.featureTitle}>Continue usando o app</div>
            <div style={styles.featureDesc}>
              Seu WhatsApp Business no celular funciona normalmente
            </div>
          </div>
        </div>
        
        <div style={styles.featureItem}>
          <div style={{ ...styles.featureIcon, backgroundColor: '#1877f215' }}>
            <MessageSquare size={20} color="#1877f2" />
          </div>
          <div style={styles.featureContent}>
            <div style={styles.featureTitle}>Mensagens sincronizadas</div>
            <div style={styles.featureDesc}>
              Envie e receba por ambos - tudo sincronizado
            </div>
          </div>
        </div>
        
        <div style={styles.featureItemLast}>
          <div style={{ ...styles.featureIcon, backgroundColor: '#f59e0b15' }}>
            <History size={20} color="#f59e0b" />
          </div>
          <div style={styles.featureContent}>
            <div style={styles.featureTitle}>Histórico importado</div>
            <div style={styles.featureDesc}>
              Até 180 dias de conversas anteriores
            </div>
          </div>
        </div>
      </div>

      <button
        style={{
          ...styles.connectBtn,
          ...(!sdkReady ? styles.connectBtnDisabled : {}),
        }}
        onClick={launchWhatsAppSignup}
        disabled={!sdkReady}
      >
        {!sdkReady ? (
          <>
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
            Carregando...
          </>
        ) : (
          <>
            <WhatsAppLogo size={24} color="white" />
            Conectar WhatsApp Business
            <ArrowRight size={20} />
          </>
        )}
      </button>
    </>
  );

  // ============================================
  // RENDER: EXCHANGING (WA Business)
  // ============================================
  const renderExchanging = () => (
    <>
      <div style={styles.iconContainer}>
        <Loader2 size={40} color="#25D366" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
      <h2 style={styles.title}>Configurando conta...</h2>
      <p style={styles.subtitle}>Isso pode levar alguns segundos</p>
    </>
  );

  // ============================================
  // RENDER: RECREATING
  // ============================================
  const renderRecreating = () => (
    <>
      <div style={styles.iconContainer}>
        <Settings size={40} color={colors.accentPrimary} style={{ animation: 'spin 2s linear infinite' }} />
      </div>
      <h2 style={styles.title}>Reconfigurando...</h2>
      <p style={styles.subtitle}>Estamos preparando um novo ambiente para você</p>
    </>
  );

  // ============================================
  // RENDER: SYNCING
  // ============================================
  const renderSyncing = () => (
    <div style={styles.syncContainer}>
      <RefreshCw 
        size={64} 
        style={{ ...styles.syncIcon, animation: 'spin 2s linear infinite' }} 
      />
      <h2 style={styles.title}>Sincronizando...</h2>
      <p style={styles.subtitle}>Buscando suas conversas e mensagens</p>
      
      <div style={styles.progressBar}>
        <div style={{ ...styles.progressFill, width: `${syncProgress}%` }} />
      </div>
      <p style={styles.progressText}>{Math.round(syncProgress)}% concluído</p>
    </div>
  );

  // ============================================
  // RENDER: CONNECTED
  // ============================================
  const renderConnected = () => (
    <>
      <CheckCircle2 size={64} style={styles.successIcon} />
      <h2 style={styles.title}>Conectado com sucesso! 🎉</h2>
      <p style={styles.subtitle}>Redirecionando para configurar seu agente...</p>
    </>
  );

  // ============================================
  // RENDER: ERROR
  // ============================================
  const renderError = () => (
    <>
      <div style={{ ...styles.iconContainer, backgroundColor: '#EF444415' }}>
        <WifiOff size={40} color="#EF4444" />
      </div>
      <h2 style={styles.title}>Erro na conexão</h2>
      <p style={styles.subtitle}>{error || 'Não foi possível conectar. Tente novamente.'}</p>
      
      <button 
        style={styles.retryButton} 
        onClick={isWABusiness ? launchWhatsAppSignup : () => handleRecreateInstance()}
      >
        <RefreshCw size={16} />
        Tentar novamente
      </button>
      {!isWABusiness && renderProviderPicker(true)}
    </>
  );

  // ============================================
  // RENDER: MAIN
  // ============================================
  const renderContent = () => {
    // ✅ CORREÇÃO: Se não tiver canal, mostrar loading (vai redirecionar via useEffect)
    if (!channel) {
      return renderLoading();
    }
    
    switch (connectionState) {
      case STATES.LOADING:
        return renderLoading();
      case STATES.WAITING_QR:
        return renderQRCode();
      case STATES.WAITING_OAUTH:
        return renderOAuth();
      case STATES.EXCHANGING:
        return renderExchanging();
      case STATES.RECREATING:
        return renderRecreating();
      case STATES.SYNCING:
        return renderSyncing();
      case STATES.CONNECTED:
        return renderConnected();
      case STATES.ERROR:
        return renderError();
      default:
        return renderLoading();
    }
  };

  return (
    <div style={styles.container}>
      <main style={styles.content}>
        <div style={styles.card}>
          {renderContent()}
        </div>
      </main>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ConnectionSetup;